import { Injectable } from '@angular/core';
import { getContrastingTextColor, isValidHexColor, normalizeHexColor } from '../app/shared/utils/color.utils';

/**
 * Service to manage dynamic theming based on the active show's brand colors.
 * Updates Ionic CSS custom properties to apply show-specific colors throughout the app.
 */
@Injectable({
    providedIn: 'root'
})
export class ThemeService {

    private readonly defaultPrimaryColor = '#3880ff';
    private currentShowColor: string | null = null;

    /**
     * Applies the show's primary color as the app's primary theme color.
     * This affects all Ionic components that use color="primary".
     *
     * @param primaryColor - Hex color string (e.g., '#FF5733') or null to reset
     */
    public applyShowTheme(primaryColor: string | null): void {
        if (!primaryColor || !isValidHexColor(primaryColor)) {
            this.resetToDefaultTheme();
            return;
        }

        const color = normalizeHexColor(primaryColor);

        // Don't reapply if already set
        if (this.currentShowColor === color) {
            return;
        }

        this.currentShowColor = color;
        this.setIonicPrimaryColor(color);
    }

    /**
     * Resets the theme to the default app primary color.
     */
    public resetToDefaultTheme(): void {
        if (this.currentShowColor === null) {
            return;
        }
        this.currentShowColor = null;
        this.setIonicPrimaryColor(this.defaultPrimaryColor);
    }

    /**
     * Sets the Ionic primary color CSS custom properties.
     */
    private setIonicPrimaryColor(hexColor: string): void {
        const rgb = this.hexToRgb(hexColor);
        const contrastColor = getContrastingTextColor(hexColor);
        const contrastRgb = this.hexToRgb(contrastColor);
        const shade = this.shadeColor(hexColor, -12);
        const tint = this.shadeColor(hexColor, 12);

        const root = document.documentElement;

        root.style.setProperty('--ion-color-primary', hexColor);
        root.style.setProperty('--ion-color-primary-rgb', rgb);
        root.style.setProperty('--ion-color-primary-contrast', contrastColor);
        root.style.setProperty('--ion-color-primary-contrast-rgb', contrastRgb);
        root.style.setProperty('--ion-color-primary-shade', shade);
        root.style.setProperty('--ion-color-primary-tint', tint);
    }

    /**
     * Converts a hex color to RGB string format (e.g., "255, 87, 51").
     */
    private hexToRgb(hex: string): string {
        const color = hex.replace('#', '');
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        return `${r}, ${g}, ${b}`;
    }

    /**
     * Lightens or darkens a hex color by a percentage.
     *
     * @param hex - The hex color to modify
     * @param percent - Positive to lighten, negative to darken
     */
    private shadeColor(hex: string, percent: number): string {
        const color = hex.replace('#', '');
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);

        const adjust = (value: number): number => {
            const adjusted = value + (value * percent / 100);
            return Math.min(255, Math.max(0, Math.round(adjusted)));
        };

        const newR = adjust(r).toString(16).padStart(2, '0');
        const newG = adjust(g).toString(16).padStart(2, '0');
        const newB = adjust(b).toString(16).padStart(2, '0');

        return `#${newR}${newG}${newB}`;
    }
}
